import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Alert,
} from "@mui/material";
import { Wrapper } from "../../components";
import { useGetProofupUrlMutation } from "src/store/services/proofupApi";
import {
  useGetApplicationsMutation,
  useSaveApplicationDataMutation,
} from "src/store/services/privateApi";
import { useChildPopup } from "src/shared/utilities/usePopup";
import { ProofupHelpVideo } from "src/components/Media/ProofupHelpVideo/ProofupHelpVideo";

export const IncomeVerification = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const leaseId = searchParams.get("leaseid");
  const personId = searchParams.get("personid");
  const propertyId = searchParams.get("propertyid");
  const applicationId = searchParams.get("applicationId");

  const [error, setError] = useState<string>("");
  const { openPopup, status, isLoading } = useChildPopup();

  const [getProofupUrl] = useGetProofupUrlMutation();
  const [getApplications] = useGetApplicationsMutation();
  const [saveApplicationData] = useSaveApplicationDataMutation();

  const [proofupAppUrl, setProofupAppUrl] = useState<string>("");
  const [applicationLoaded, setApplicationLoaded] = useState(!applicationId);
  const [isAffordableApplication, setIsAffordableApplication] = useState(false);
  // Full applicationData, kept so we can mark proofupStatus completed without
  // clobbering the rest of the blob when the applicant resumes here.
  const [appData, setAppData] = useState<any>(null);

  useEffect(() => {
    if (!leaseId) {
      setError("Invalid lease ID. Please go back and try again.");
    } else if (!applicationLoaded) {
      return;
    } else if (isAffordableApplication) {
      setError(
        "Income verification for affordable applications is handled by the property team."
      );
    } else {
      fetchProofupUrl();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaseId, applicationLoaded, isAffordableApplication]);

  // Load the application so completion can be persisted (merged, not clobbered).
  useEffect(() => {
    if (applicationId) {
      getApplications({ id: applicationId })
        .then((res: any) => {
          const apps = res?.data;
          const app = Array.isArray(apps)
            ? apps.find((a: any) => String(a.id) === String(applicationId)) ??
              apps[0]
            : apps;
          if (app) {
            setAppData(app.applicationData ?? {});
            setIsAffordableApplication(
              app.property?.affordableHousing === true
            );
          }
          setApplicationLoaded(true);
        })
        .catch(() => {
          // Non-fatal: without appData we just skip persisting completion; the
          // resume CTA stays so the applicant can retry.
          setApplicationLoaded(true);
        });
    }
  }, [applicationId, getApplications]);

  // When verification completes, mark proofupStatus completed so the resume CTA
  // clears from the home screen.
  useEffect(() => {
    if (status === "success" && applicationId && appData) {
      if (appData.proofupStatus !== "completed") {
        const nextAppData = { ...appData, proofupStatus: "completed" };
        setAppData(nextAppData);
        saveApplicationData({
          id: applicationId,
          body: { applicationData: nextAppData },
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, applicationId, appData]);

  const fetchProofupUrl = async () => {
    const proofupUrl = await getProofupUrl({
      leaseId,
      personId,
      propertyId,
    }).unwrap();
    const link = proofupUrl.data.link;

    setProofupAppUrl(link);
  };

  // The applicant reaches this page post-payment to finish (or retry) income
  // verification, so completion returns them home rather than to payment.
  const handleContinueToPayment = () => {
    navigate(`/`);
  };

  if (!leaseId) {
    return (
      <Wrapper>
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h5" color="error" gutterBottom>
            {t("account.incomeVerification.invalidRequestTitle")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t("account.incomeVerification.invalidRequestMessage")}
          </Typography>
          <Button variant="contained" onClick={() => navigate("/")}>
            {t("account.incomeVerification.goHomeButton")}
          </Button>
        </Box>
      </Wrapper>
    );
  }

  return (
    <>
      <Wrapper>
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 3,
          }}
        >
          <Card sx={{ maxWidth: 600, width: "100%" }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h4" gutterBottom textAlign="center">
                {t("account.incomeVerification.title")}
              </Typography>

              <Typography variant="h6" sx={{ mb: 2, color: "primary.main" }}>
                {t("account.incomeVerification.subtitle")}
              </Typography>

              <Typography variant="body1" sx={{ mb: 3 }}>
                {t("account.incomeVerification.description")}
              </Typography>

              <ProofupHelpVideo />

              <Box sx={{ mb: 3, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "medium", mb: 1 }}
                >
                  {t("account.incomeVerification.whatYouNeedTitle")}
                </Typography>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  <li>{t("account.incomeVerification.needItem1")}</li>
                  <li>{t("account.incomeVerification.needItem2")}</li>
                  <li>{t("account.incomeVerification.needItem3")}</li>
                </ul>
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              {/* {verificationStarted && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  Income verification window has been opened. Complete the
                  process in the new window, then return here to continue.
                </Alert>
              )} */}

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={() =>
                    status === "success"
                      ? handleContinueToPayment()
                      : openPopup(proofupAppUrl)
                  }
                  disabled={isLoading || !proofupAppUrl}
                  fullWidth
                >
                  {status === "opening" &&
                    t("account.incomeVerification.openingButton")}
                  {status === "in_progress" &&
                    t("account.incomeVerification.waitingButton")}
                  {status === "idle" &&
                    t("account.incomeVerification.startButton")}
                  {status === "success" &&
                    t("account.incomeVerification.verifiedButton")}
                  {status === "error" &&
                    t("account.incomeVerification.retryButton")}
                  {status === "closed" &&
                    t("account.incomeVerification.retryButton")}
                </Button>

                {/* <Button
                  variant="outlined"
                  size="large"
                  onClick={handleSkipForNow}
                  fullWidth
                >
                  {t("account.incomeVerification.skipButton")}
                </Button> */}
              </Box>

              <Typography
                variant="body2"
                sx={{ mt: 3, textAlign: "center", color: "text.secondary" }}
              >
                {t("account.incomeVerification.securityNote")}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Wrapper>
    </>
  );
};
